import { ASSET_EVENT_TIMEOUT_MS } from '@/shared/utils/asset-event-wait/asset-event-wait.constants';
import {
  TAssetEventOutcome,
  TAssetEventTarget,
} from '@/shared/utils/asset-event-wait/asset-event-wait.types';

const pendingEvents = new Map<string, Set<() => void>>();

const getKey = ({ assetType, assetId }: TAssetEventTarget) =>
  `${assetType}:${assetId}`;

export const notifyAssetEvent = (target: TAssetEventTarget) => {
  pendingEvents.get(getKey(target))?.forEach((resolveEvent) => resolveEvent());
};

const waitWithTimeout = (received: Promise<void>) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  // The event can go missing (dropped socket, no publish) and callers block a loader on it.
  const timedOut = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => resolve(false), ASSET_EVENT_TIMEOUT_MS);
  });

  return Promise.race([received.then(() => true), timedOut]).finally(() =>
    clearTimeout(timer),
  );
};

// The wait is registered before `work` runs: the asset id is known long before
// the upload it belongs to ends, so the event can land mid-work.
export const awaitAssetEvent = async <TResult>(
  target: TAssetEventTarget,
  work: () => Promise<TResult>,
): Promise<TAssetEventOutcome<TResult>> => {
  const key = getKey(target);
  let resolveEvent!: () => void;
  const received = new Promise<void>((resolve) => {
    resolveEvent = resolve;
  });

  // A Set per key: two callers on the same asset must not unregister each other.
  const waiters = pendingEvents.get(key) ?? new Set<() => void>();
  waiters.add(resolveEvent);
  pendingEvents.set(key, waiters);

  try {
    const result = await work();
    const eventReceived = await waitWithTimeout(received);

    return { result, eventReceived };
  } finally {
    waiters.delete(resolveEvent);

    if (waiters.size === 0) {
      pendingEvents.delete(key);
    }
  }
};
